import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dumbbell, Send, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { SectionCard } from "./identityForm";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import LoadFailureNote from "./LoadFailureNote";
import { assignDrill, fetchAssignedDrills, fetchMyDrills, withdrawDrill } from "../lib/drills";

/**
 * Issues #461 → #509 — a coach's drills for THIS player: assignment only.
 *
 * #461 built this panel with the author form inline (create + edit +
 * assign, all here on Coach Portal → player → Guides tab). #509 split
 * that: AUTHORING moved to the drill bank on the coach's Training surface
 * (`CoachDrillBankPanel.jsx`, mounted on `/learn?tab=training` — a drill
 * is account-scoped, so writing one never needed a player selected), and
 * this panel keeps what actually is per-player: pick a drill from the
 * bank, assign it, withdraw it. The caption below points authoring at
 * Training, and deliberately promises drills only — reviewed guides are
 * the #98 pipeline and are not coach-authored anywhere.
 *
 * THE PICKER (#509 point 3): a filterable LIST, not a popper Select.
 * Decided from the data: `coach_drills` has no category to group by
 * (deliberately — the #461 scope note), so #503's SelectGroup treatment
 * has nothing to group on; what the bank does have is recency, and the
 * server already returns newest-first (`listDrillsForCoach`), which this
 * list preserves without a comparator of its own. A text filter over the
 * title gets a named drill out of a 30-drill bank in two interactions
 * (type, then Assign). Staying a list also keeps the control in-viewport
 * by construction — the exact failure #511 diagnosed on the guide
 * Select's popper one card up is unbuildable here.
 *
 * SELF-CONTAINED on purpose, the `PlayerIdentityPanel` shape: the page
 * passes WHO (`playerId`, `playerName`) and this panel owns its fetches
 * and writes — `CoachPortal.jsx` has a per-function line budget this
 * panel must not spend.
 *
 * THE BANK FETCH IS NOT SWALLOWED (#511's second bug, the #271 class): a
 * failed `GET /drills` renders LoadFailureNote + retry, and "no drills"
 * copy renders only once the bank actually arrived. The per-player
 * ASSIGNED fetch keeps the page's collapse-to-empty posture — it drives
 * only which button a row shows, never an emptiness claim.
 *
 * Every rule is enforced server-side: the API refuses a player writer, a
 * foreign drill, an unlinked player. Hiding a button is a courtesy, not a
 * control.
 */
const TOUCH = "min-h-[44px]";

export default function CoachDrillsPanel({ playerId, playerName, appearance }) {
  const [drills, setDrills] = useState([]);
  // "loading" | "loaded" | "failed" — "empty" may only be claimed from
  // "loaded" (#271/#511, the #284 rosterLoaded shape).
  const [bankStatus, setBankStatus] = useState("loading");
  const [retryNonce, setRetryNonce] = useState(0);
  const [filter, setFilter] = useState("");
  const [assigned, setAssigned] = useState([]);

  useEffect(() => {
    let cancelled = false;
    setBankStatus("loading");
    fetchMyDrills()
      .then((rows) => {
        if (cancelled) return;
        setDrills(rows);
        setBankStatus("loaded");
      })
      .catch(() => {
        if (!cancelled) setBankStatus("failed");
      });
    return () => { cancelled = true; };
  }, [retryNonce]);

  useEffect(() => {
    let cancelled = false;
    setAssigned([]);
    if (playerId) {
      fetchAssignedDrills(playerId)
        .then((rows) => { if (!cancelled) setAssigned(rows); })
        .catch(() => {});
    }
    return () => { cancelled = true; };
  }, [playerId]);

  const reloadAssigned = () =>
    fetchAssignedDrills(playerId).then(setAssigned).catch(() => setAssigned([]));

  /** assignment row for a drill, or undefined — drives Assign vs Withdraw. */
  const assignmentFor = (drillId) => assigned.find((a) => a.drill_id === drillId);

  const assignExisting = async (drill) => {
    try {
      await assignDrill(playerId, drill.drill_id);
      await reloadAssigned();
      toast.success(`Drill assigned to ${playerName}`);
    } catch {
      toast.error("Could not assign that drill. Refresh and try again.");
    }
  };

  const withdraw = async (assignment) => {
    try {
      await withdrawDrill(assignment.assignment_id);
      await reloadAssigned();
    } catch {
      toast.error("Could not withdraw that drill. Refresh and try again.");
    }
  };

  // Title-only match, case-insensitive: the acceptance ask is "find a NAMED
  // drill", and matching the body too would surface rows whose visible
  // title shows nothing of the query.
  const query = filter.trim().toLowerCase();
  const visible = query ? drills.filter((d) => d.title.toLowerCase().includes(query)) : drills;

  // Issue #526 — the drill block folds like the two guide sections above
  // it, with a `count` of the drills in the coach's bank. The bank and
  // assigned fetches live in THIS component, outside the fold, so the
  // folded card still knows its count (the SectionCard rule: data loading
  // lives in the calling section); the `key={playerId}` on the card resets
  // the fold to closed on a roster click WITHOUT remounting the panel, so
  // the account-scoped bank is not refetched per player.
  return (
    <SectionCard
      key={playerId}
      appearance={appearance}
      icon={Dumbbell}
      title="Assign a drill"
      desc={`Pick one of your drills — ${playerName} sees it on Training & Guides, alongside any assigned guides, with your name on it.`}
      collapsible
      defaultOpen={false}
      count={drills.length}
      toggleTestid="cp-drills-toggle"
      testid="cp-drills-panel"
    >
        <div>
          {/* #509 point 4: where drills come FROM. The copy promises drills
              only — reviewed guides are the #98 review pipeline, and no
              surface lets a coach author one. */}
          <p className="text-xs text-slate-500" data-testid="cp-drill-bank-caption">
            Drills are created in the{" "}
            <Link to="/learn?tab=training" className="underline text-[#0B2E59] font-medium" data-testid="cp-drill-bank-link">
              Training
            </Link>{" "}
            section's drill bank — write one there and it appears here for every player. Reviewed guides
            can't be authored there; drills only.
          </p>
        </div>

        <div className="space-y-2" data-testid="cp-own-drills">
          <div className="text-[11px] uppercase tracking-widest text-slate-500">Your drills — newest first</div>

          {bankStatus === "failed" && (
            <LoadFailureNote
              what="your drills"
              testId="cp-drills-error"
              onRetry={() => setRetryNonce((n) => n + 1)}
            />
          )}

          {bankStatus === "loaded" && drills.length === 0 && (
            <p className="text-sm text-slate-500" data-testid="cp-no-drills">
              No drills in your bank yet. Create one in the{" "}
              <Link to="/learn?tab=training" className="underline">Training</Link> section and it will be
              ready to assign here.
            </p>
          )}

          {drills.length > 0 && (
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Find a drill by title…"
              className={TOUCH}
              data-testid="cp-drill-filter"
            />
          )}

          {drills.length > 0 && visible.length === 0 && (
            <p className="text-sm text-slate-500" data-testid="cp-drill-filter-empty">
              No drill title matches “{filter.trim()}”.
            </p>
          )}

          {visible.map((d) => {
            const assignment = assignmentFor(d.drill_id);
            return (
              <div key={d.drill_id} className="rounded-xl border border-slate-200 p-3" data-testid={`cp-drill-${d.drill_id}`}>
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm text-slate-900">{d.title}</div>
                    <p className="text-xs text-slate-500 mt-0.5 whitespace-pre-wrap">{d.body}</p>
                    {assignment && (
                      <div className="text-[11px] mt-1.5 font-medium" style={{ color: "#10B981" }} data-testid={`cp-drill-assigned-${d.drill_id}`}>
                        Assigned to {playerName}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {assignment ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => withdraw(assignment)}
                        aria-label={`Withdraw ${d.title}`}
                        data-testid={`cp-drill-withdraw-${d.drill_id}`}
                      >
                        <Undo2 size={14} className="text-slate-400" />
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => assignExisting(d)}
                        aria-label={`Assign ${d.title} to ${playerName}`}
                        data-testid={`cp-drill-assign-${d.drill_id}`}
                      >
                        <Send size={14} className="mr-1" /> Assign
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
    </SectionCard>
  );
}
